import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonText,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { DecaCompanyRole, IDecaDocumentCompany } from '@okcargo-apps/shared-domain';
import { IPlace } from '@okcargo-apps/shared-domain-ionic';
import { map, startWith, switchMap } from 'rxjs';
import { SearchCityPostalcodeModalComponent } from '../../freight-shipper';
import { DecaContactBookModalComponent } from '../deca-contact-book-modal/deca-contact-book-modal.component';
import { hasDecaCompanyData } from '../deca-forms.utils';
import { DecaCompanyFormModel, DecaCompanyMode, IDecaCompanyFormValue, IStopLocationData } from '../deca-forms.model';
import { DECA_ROLE } from '../deca-role.utils';

@Component({
  selector: 'lib-deca-edit-company',
  templateUrl: './deca-edit-company.component.html',
  imports: [
    IonText,
    IonGrid,
    IonLabel,
    IonItem,
    IonIcon,
    IonButton,
    IonRow,
    IonCol,
    IonInput,
    ReactiveFormsModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecaEditCompanyComponent {
  public form = input.required<FormGroup<DecaCompanyFormModel>>();
  public locationData = input<IStopLocationData | null>(null);
  public viewMode = input.required<DecaCompanyMode>();
  public companyRole = input.required<DecaCompanyRole>();
  public readOnly = input(false);

  public readonly locationChange = output<IStopLocationData | null>();
  public readonly modeChange = output<DecaCompanyMode>();
  public readonly setManualMode = output<void>();

  private readonly modalCtrl = inject(ModalController);

  public readonly locationSignal = linkedSignal<IStopLocationData | null>(() => this.locationData());

  // A read only company (edits of an already started service) is always shown as a summary
  protected readonly currentViewMode = computed<DecaCompanyMode>(() => (this.readOnly() ? 'summary' : this.viewMode()));

  protected readonly isFormDisabled = computed(() => this.form().disabled);

  private readonly formValue = toSignal(
    toObservable(this.form).pipe(
      switchMap((form) =>
        form.valueChanges.pipe(
          startWith(null),
          map((): IDecaCompanyFormValue => form.getRawValue()),
        ),
      ),
    ),
    { initialValue: {} },
  );

  protected readonly hasData = computed(() => hasDecaCompanyData(this.formValue(), this.locationSignal()));

  public createNewCompany(): void {
    const form = this.form();

    form.patchValue({
      companyName: '',
      taxId: '',
      street: '',
      contactEmail: '',
      contactFirstName: '',
      contactLastName: '',
    });
    form.markAsPristine();
    form.markAsUntouched();
    form.updateValueAndValidity();

    this.locationSignal.set(null);
    this.locationChange.emit(null);
    this.modeChange.emit('manual');
  }

  private readonly formStateChanges = toSignal(
    toObservable(this.form).pipe(
      switchMap((form) =>
        form.events.pipe(
          startWith(null),
          map(() => ({
            invalid: form.invalid,
            touched: form.touched,
            emailValue: form.controls.contactEmail.value,
          })),
        ),
      ),
    ),
    {
      initialValue: { invalid: false, touched: false, emailValue: '' },
    },
  );

  protected hasEssentialDataError = computed(() => {
    const state = this.formStateChanges();

    if (!state.touched) {
      return false;
    }

    const postalCode = this.locationSignal()?.postalCode?.trim();
    const isLocationMissing = !postalCode;

    const hasFormErrors = state.touched && state.invalid;

    if (this.companyRole() === DECA_ROLE.SHIPPER) {
      return hasFormErrors || isLocationMissing;
    } else {
      const hasEmail = !!state.emailValue?.trim();
      if (hasEmail) {
        return hasFormErrors || isLocationMissing;
      }
      return hasFormErrors;
    }
  });

  public async openContactBookModal(): Promise<void> {
    const modal = await this.modalCtrl.create({ component: DecaContactBookModalComponent });
    await modal.present();
    const { data, role } = await modal.onWillDismiss<IDecaDocumentCompany>();

    if (role === 'confirm' && data) {
      const cleanFormPayload = Object.fromEntries(
        Object.entries({
          companyName: data.companyName,
          taxId: data.taxId,
          street: data.street,
          contactEmail: data.contactEmail,
          contactFirstName: data.contactFirstName || '',
          contactLastName: data.contactLastName || '',
          contactPhone: data.contactPhone || '',
        }).filter(([_, v]) => v !== undefined),
      );

      this.form().patchValue(cleanFormPayload, { emitEvent: true });
      this.form().markAsDirty();
      this.form().markAsTouched();
      this.form().updateValueAndValidity();

      const locationPayload: IStopLocationData = {
        city: data.city || '',
        postalCode: data.postalCode || '',
        stateCode: data.stateCode || '',
        countryCode: data.countryCode || '',
      };

      this.locationSignal.set(locationPayload);
      this.locationChange.emit(locationPayload);
      this.modeChange.emit('summary');
    }
  }

  public async openPostalCodeModal(): Promise<void> {
    const modal = await this.modalCtrl.create({ component: SearchCityPostalcodeModalComponent });
    await modal.present();
    const { data, role } = await modal.onWillDismiss<IPlace>();

    if (role === 'confirm' && data) {
      this.form().markAsDirty();
      this.form().markAsTouched();
      this.form().updateValueAndValidity();

      const locationPayload: IStopLocationData = {
        city: data.city,
        postalCode: data.postalCode,
        stateCode: data.stateCode,
        countryCode: data.countryCode,
      };

      this.locationSignal.set(locationPayload);
      this.locationChange.emit(locationPayload);
    }
  }
}
